import React, { Suspense, lazy, useEffect, ComponentType, FunctionComponent } from 'react'
import { Navigate, useNavigate, useLocation, RouteObject } from 'react-router-dom'

import { Register, AuthGuard, DEFAULT_ROUTES, GuestGuard, pageTypes } from 'torqcloud-ui'

import { AppLayout } from './components/Layout'
import { organizationAuditLog, organizationSettings, organizationUsage, playground } from './constants/routing'
import { UiActionProvider } from './contexts/uiActions/UiActionContext'
import { skillResource } from './pages/skill_editor/skillResource'
import { teammateResource } from './pages/teammate/teammateResource'
import { userToolResource } from './pages/tool_builder/userToolResource'

const uiActionResources = [skillResource, teammateResource, userToolResource]
import { ModelDataSecurityProvider } from './contexts/ModelDataSecurityContext'
import ApiKeys from './pages/apikeys'
import EmailVerificationPage from './pages/auth/email-verification/EmailVerificationPage'
import JoinOrganizationPage from './pages/auth/join-organization/JoinOrganizationPage'
import OrganizationStatusGuard from './components/OrganizationStatusGuard'
import OrganizationSettingsGuard from './components/OrganizationSettingsGuard'
import OrganizationUsageGuard from './components/OrganizationUsageGuard'
import OrganizationAuditLogGuard from './components/OrganizationAuditLogGuard'
import { appConfig } from './config'

function Loadable<TComponent extends ComponentType<TProps>, TProps>(
  Component: ComponentType<TProps>
): FunctionComponent<TProps & React.JSX.IntrinsicAttributes & React.JSX.LibraryManagedAttributes<TComponent, TProps>> {
  return (props: TProps & React.JSX.IntrinsicAttributes & React.JSX.LibraryManagedAttributes<TComponent, TProps>) => (
    <Suspense fallback={null}>
      <Component {...props} />
    </Suspense>
  )
}

// Account pages
const Account = Loadable(lazy(() => import('./pages/Account')))
const OrganizationSettings = Loadable(lazy(() => import('./pages/OrganizationSettings')))
const OrganizationUsage = Loadable(lazy(() => import('./pages/usage/OrganizationUsage')))
const OrganizationAuditLog = Loadable(lazy(() => import('./pages/audit/OrganizationAuditLog')))

const MicrosoftOAuthCallback = Loadable(lazy(() => import('./pages/oauth/MicrosoftOAuthCallback')))

// Teammate pages
const Teammates = Loadable(lazy(() => import('./pages/teammates')))
const TeammateDialog = Loadable(lazy(() => import('./pages/teammates/dialog')))
const TeammatePage = Loadable(lazy(() => import('./pages/teammate/TeammatePage')))
const Playground = Loadable(lazy(() => import('./pages/playground')))
const Memory = Loadable(lazy(() => import('./pages/memory')))

const TeammateLayout = Loadable(lazy(() => import('./pages/teammates/layout')))
const TeammateEdit = Loadable(lazy(() => import('./pages/teammates/edit')))

// Templates
const Templates = Loadable(lazy(() => import('./pages/templates')))

// Tools Pages
const UserTools = Loadable(lazy(() => import('./pages/tool_builder/UserTools')))
const UserTool = Loadable(lazy(() => import('./pages/tool_builder/UserToolRoute')))

// Skills Pages
const Skills = Loadable(lazy(() => import('./pages/skills/Skills')))
const SkillEditor = Loadable(lazy(() => import('./pages/skill_editor/SkillEditorRoute')))

// Team
const TeamsMarketPlace = Loadable(lazy(() => import('./pages/teams/TeamsMarketplace')))
const TeamPage = Loadable(lazy(() => import('./pages/teams/TeamPage')))

// Error pages
const NotFound = Loadable(lazy(() => import('./pages/NotFound')))

// Legacy routes redirect
const LegacyRedirect = () => {
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    const path = location.pathname
    const pathMappings = {
      '/my-agents': '/teammates', // Legacy
      '/my-teammates': '/teammates', // Update from TORQ-1549
      '/my-teammates/new': '/teammates/new',
      '/my-templates': '/templates', // Update from TORQ-1549
    }

    const matchingLegacyPath = Object.keys(pathMappings).find(oldPath => path.startsWith(oldPath))

    if (matchingLegacyPath) {
      const redirectPath = path.replace(matchingLegacyPath, pathMappings[matchingLegacyPath])
      navigate(redirectPath, { replace: true })
    }
  }, [navigate, location])

  return null
}

const routes: RouteObject[] = [
  // Legacy redirects — evaluated before the authenticated shell
  { path: 'my-agents/*', element: <LegacyRedirect /> },
  { path: 'agents/*', element: <LegacyRedirect /> },
  { path: 'my-teammates', element: <LegacyRedirect /> },
  { path: 'my-teammates/new', element: <LegacyRedirect /> },
  { path: 'my-teammates/*', element: <LegacyRedirect /> },

  // AppLayout mounts once, only <Outlet> changes, preventing layout flashing
  {
    element: (
      <AuthGuard>
        <OrganizationStatusGuard>
          <UiActionProvider resources={uiActionResources}>
            <ModelDataSecurityProvider>
              <AppLayout />
            </ModelDataSecurityProvider>
          </UiActionProvider>
        </OrganizationStatusGuard>
      </AuthGuard>
    ),
    children: [
      { path: 'account', element: <Account />, handle: { title: 'Account', pageName: pageTypes.ACCOUNT } },
      {
        path: organizationSettings().replace('/', ''),
        element: (
          <OrganizationSettingsGuard>
            <OrganizationSettings />
          </OrganizationSettingsGuard>
        ),
        // No pageName here: OrganizationSettings tracks its own page view so it
        // can report the active tab, which the central hook cannot see.
        handle: { title: 'Organization Settings' },
      },
      {
        // A sibling of Organization Settings rather than a child: its guard requires manage_organization,
        // while Usage is also open to admins.
        path: organizationUsage().replace('/', ''),
        element: (
          <OrganizationUsageGuard>
            <OrganizationUsage />
          </OrganizationUsageGuard>
        ),
        handle: { title: 'Usage', pageName: pageTypes.ORGANIZATION_USAGE },
      },
      {
        // A sibling of Usage for the same reason: admins and managers see it without managing the organization.
        path: organizationAuditLog().replace('/', ''),
        element: (
          <OrganizationAuditLogGuard>
            <OrganizationAuditLog />
          </OrganizationAuditLogGuard>
        ),
        handle: { title: 'Audit Log', pageName: pageTypes.ORGANIZATION_AUDIT_LOG },
      },
      { path: 'playground', element: <Playground />, handle: { title: 'Playground', pageName: pageTypes.PLAYGROUND } },
      { path: 'memory', element: <Memory />, handle: { title: 'Memory', pageName: pageTypes.MEMORY } },
      { path: 'templates', element: <Templates />, handle: { title: 'Templates', pageName: pageTypes.TEMPLATES } },
      {
        path: 'teammates',
        children: [
          { index: true, element: <Teammates />, handle: { title: 'Teammates', pageName: pageTypes.MY_CHATBOT_AGENTS } },
          {
            element: <TeammatePage />,
            children: [
              {
                element: <TeammateLayout />,
                children: [
                  {
                    path: 'new',
                    element: <TeammateEdit />,
                    handle: { title: 'Teammate Builder', pageName: pageTypes.EDIT_CHATBOT_AGENT },
                  },
                  {
                    path: ':id',
                    element: <TeammateEdit />,
                    handle: { title: 'Teammate Builder', pageName: pageTypes.EDIT_CHATBOT_AGENT },
                  },
                  {
                    path: ':id/dialog',
                    element: <TeammateDialog />,
                    handle: { title: 'Teammates', pageName: pageTypes.CHATBOT_AGENT_DIALOG },
                  },
                ],
              },
            ],
          },
        ],
      },
      { path: 'user-tools', element: <UserTools />, handle: { title: 'Tools', pageName: pageTypes.TOOLS_VIEWER } },
      { path: 'user-tools/new', element: <UserTool />, handle: { title: 'Tools', pageName: pageTypes.TOOLS_VIEWER } },
      { path: 'user-tools/:id', element: <UserTool />, handle: { title: 'Tools', pageName: pageTypes.TOOLS_VIEWER } },
      { path: 'skills', element: <Skills />, handle: { title: 'Skills', pageName: pageTypes.SKILLS_LIST } },
      { path: 'skills/new', element: <SkillEditor />, handle: { title: 'Skills', pageName: pageTypes.SKILL_EDITOR } },
      { path: 'skills/:id', element: <SkillEditor />, handle: { title: 'Skills', pageName: pageTypes.SKILL_EDITOR } },
      {
        path: 'teams',
        children: [
          { index: true, element: <TeamsMarketPlace />, handle: { title: 'Teams', pageName: pageTypes.TEAMS_LIST } },
          // /teams/new renders the build tab, so it reports the same page as /teams/:id/build.
          { path: 'new', element: <TeamPage />, handle: { title: 'Teams', pageName: pageTypes.TEAM_BUILD } },
          { path: 'shared-teams', element: <TeamsMarketPlace />, handle: { title: 'Teams', pageName: pageTypes.TEAMS_LIST } },
          { path: ':id', element: <TeamPage />, handle: { title: 'Teams' } },
          { path: ':id/build', element: <TeamPage />, handle: { title: 'Teams', pageName: pageTypes.TEAM_BUILD } },
          // No pageName here: this route immediately redirects to the canonical
          // /teams/:id/runs/:runId, which is what reports the Team Run page view.
          { path: ':id/run', element: <TeamPage />, handle: { title: 'Teams' } },
          { path: ':id/runs/:runId', element: <TeamPage />, handle: { title: 'Teams', pageName: pageTypes.TEAM_RUN } },
          { path: ':id/history', element: <TeamPage />, handle: { title: 'Teams', pageName: pageTypes.TEAM_HISTORY } },
          { path: ':id/schedule', element: <TeamPage />, handle: { title: 'Teams', pageName: pageTypes.TEAM_SCHEDULE } },
          // Config history (revisions and Team events); /teams/:id/history stays run history.
          { path: ':id/changes', element: <TeamPage />, handle: { title: 'Teams', pageName: pageTypes.TEAM_CHANGES } },
        ],
      },
      { path: 'apikeys', element: <ApiKeys />, handle: { title: 'Keys & Secrets', pageName: pageTypes.API_KEYS } },
    ],
  },

  // Auth routes without app shell
  ...DEFAULT_ROUTES,
  {
    path: 'register',
    element: (
      <GuestGuard>
        <Register />
      </GuestGuard>
    ),
  },
  {
    path: 'verify-email',
    element: appConfig.enableDomainBasedRegistration ? (
      <GuestGuard>
        <EmailVerificationPage />
      </GuestGuard>
    ) : (
      <Navigate to="/" replace />
    ),
  },
  {
    path: 'join-organization',
    element: (
      <AuthGuard>
        <JoinOrganizationPage />
      </AuthGuard>
    ),
  },

  { path: 'oauth/ms/callback', element: <MicrosoftOAuthCallback /> },

  // Catch-all
  {
    path: '*',
    children: [
      { path: '', element: <Navigate to={playground()} replace /> },
      { path: '*', element: <NotFound /> },
    ],
  },
]

export default routes
